"use client";

import React from "react";
import { CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, ArrowRight } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "detection",
  question: "How do technicians detect mold concealed inside walls or under flooring?",
  answer: "Our field technicians use non-invasive infrared thermal imaging to identify hidden temperature differentials caused by trapped moisture pockets. We follow up with calibrated pin and non-penetrating moisture meters along with wall-cavity air testing or fiber-optic borescope inspection. This pinpoints damp insulation, wet subfloors, and active microbial colonies without needlessly tearing open unaffected drywall."
}, {
  id: "health-risks",
  question: "What specific health risks does airborne mold present to occupants?",
  answer: "Active mold colonies release microscopic spores, mycotoxins, and volatile organic compounds (VOCs) directly into living spaces. When inhaled, these can trigger persistent allergic reactions, nasal congestion, coughing, eye irritation, skin rashes, and severe respiratory flare-ups in individuals with asthma or compromised immune systems. Containing spores with sealed negative-pressure HEPA filtration prevents this contamination from circulating throughout your HVAC ducts."
}, {
  id: "timeline",
  question: "How long does an average residential or commercial remediation take?",
  answer: "Most contained residential mold remediations take between two and four business days. This timeframe covers establishing sealed poly containment zones, negative-air scrubbing, active antimicrobial treatment, HEPA vacuuming, and complete structural dry-out. Larger commercial footprints or projects requiring controlled tear-out of saturated materials and third-party clearance testing typically require three to seven days depending on structural drying speed."
}, {
  id: "prevention",
  question: "What preventive steps ensure that mold will not return once remediation is complete?",
  answer: "Mold cannot thrive without moisture. Once colonies are treated and affected materials are safely removed, we dry the framing to standard equilibrium levels below 15% moisture content. We apply commercial-grade EPA-registered antimicrobial sealants to lumber surfaces, identify and repair the original water intrusion source, and advise on indoor relative humidity control to keep ambient moisture consistently below 50%."
}];
export default function Faqs() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="faqs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Rule Bar */}
        <div className="flex items-center justify-between gap-4 border-b border-border pb-4 mb-10 text-xs font-mono text-muted-foreground uppercase tracking-wider">
          <span className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            Active Mold Containment Standards
          </span>
          <span className="hidden sm:inline">MD / DC / NOVA Field Crews</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading, Supporting text, and Emergency Help Box */}
          <motion.div className="lg:col-span-5 flex flex-col justify-between" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
                <HelpCircle className="size-3.5" />
                <span>Restoration guidance</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight text-foreground leading-tight">
                Answers to common mold questions
              </h2>

              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Discovering mold inside your living space or commercial facility brings immediate questions about health hazards, cleanup timeframes, and long-term property protection.
              </p>

              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                TriServe deploys direct local technicians across Maryland, Washington DC, and Northern Virginia equipped with commercial thermal sensors and negative-air systems to safely resolve active contamination.
              </p>
            </div>

            {/* Direct Crew Help Card */}
            <div className="mt-8 p-6 rounded-lg border border-border bg-muted/40 flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-md bg-primary/10 text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Need an immediate on-site assessment?
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Direct local dispatch with no call center delays.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild variant="default" className="w-full sm:w-auto font-semibold">
                  <a href="tel:855-435-6631" className="flex items-center justify-center gap-2">
                    <PhoneCall className="size-4" />
                    <span>855-435-6631</span>
                  </a>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/ContactRequestServicePage" className="flex items-center justify-center gap-1.5">
                    <span>Request inspection</span>
                    <ArrowRight className="size-3.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Accordion Items */}
          <motion.div className="lg:col-span-7" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }}>
            <div className="rounded-lg border border-border bg-card p-6 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="detection" className="w-full">
                {faqItems.map((item, index) => <div key={item.id} data-index={index}>
                    <AccordionItem value={item.id} className="border-b border-border py-2 last:border-b-0">
                      <AccordionTrigger className="text-base sm:text-lg font-semibold text-foreground text-left py-4 hover:no-underline hover:text-primary transition-colors cursor-pointer">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pt-1 pb-4">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}